/**
 * The Query view's Explain and snippets (design board 4: "Explain", "Save as snippet").
 * - PlanResult: the plan the server answers for any engine (`{ plan: { op, target?, facts, warn?, children }, notes,
 *   totals }`) as a tree that opens and closes, the full scans flagged, the first facts (cost, rows, index) on the line.
 * - SnippetsCard: the project's saved queries (shared: `brain/.mia/data-snippets.json`), each to load and run; editors
 *   rename and delete them. SaveSnippetDialog names a new one.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CaretDown, CaretRight, Play, PencilSimple, Trash, Warning } from '@phosphor-icons/react';
import { ConfirmDialog, DataDialog, EmptyState, errorText } from './DataCommon';
import { formatCount } from '../../utils/dataModel';

const FIRST_FACTS = new Set(['cost', 'rows', 'index', 'time', 'actual rows']);
const LANGUAGE_LABELS = { sql: 'SQL', mongo: 'Mongo', cypher: 'Cypher', redis: 'Redis', kafka: 'Kafka' };

function PlanNode({ node, depth }) {
  const [open, setOpen] = useState(depth < 6);
  const kids = node.children || [];
  const first = (node.facts || []).filter(([label]) => FIRST_FACTS.has(label));
  const rest = (node.facts || []).filter(([label]) => !FIRST_FACTS.has(label));
  return (
    <li aria-expanded={kids.length ? open : undefined} className={`data-plan-node${node.warn ? ' is-warn' : ''}`} data-testid="plan-node" role="treeitem">
      <div className="data-plan-row" style={{ paddingLeft: depth * 18 }}>
        {kids.length ? (
          <button aria-label={`${open ? 'Close' : 'Open'} ${node.op}`} className="data-btn is-ghost is-tiny" onClick={() => setOpen(!open)} type="button">
            {open ? <CaretDown aria-hidden size={11} /> : <CaretRight aria-hidden size={11} />}
          </button>
        ) : <span className="data-tree-pad" />}
        <strong>{node.op}</strong>
        {node.target && <span className="data-mono">{node.target}</span>}
        {first.map(([label, value]) => <span className="data-pill" key={label}>{label} {String(value)}</span>)}
        {node.warn && <span className="data-pill is-warn"><Warning aria-hidden size={12} />{node.warn}</span>}
      </div>
      {rest.length > 0 && (
        <dl className="data-plan-facts" style={{ paddingLeft: depth * 18 + 30 }}>
          {rest.map(([label, value]) => (
            <React.Fragment key={label}><dt>{label}</dt><dd className="data-mono">{String(value)}</dd></React.Fragment>
          ))}
        </dl>
      )}
      {kids.length > 0 && open && (
        <ul className="data-plan-list" role="group">
          {kids.map((kid, i) => <PlanNode depth={depth + 1} key={i} node={kid} />)}
        </ul>
      )}
    </li>
  );
}

PlanNode.propTypes = { depth: PropTypes.number, node: PropTypes.object.isRequired };

export function PlanResult({ plan, canMeasure = false, measuring = false, onMeasure = null }) {
  const totals = plan.totals || {};
  const summary = [
    totals.cost !== undefined ? `total cost ${totals.cost}` : '',
    totals.rows !== undefined ? `about ${formatCount(totals.rows)} row${totals.rows === 1 ? '' : 's'}` : '',
    totals.ms !== undefined ? `ran in ${totals.ms} ms` : '',
  ].filter(Boolean).join(' · ');
  return (
    <div className="data-card data-result" data-testid="query-plan">
      <div className="data-card-head">
        <strong>Plan</strong>
        <span className="data-muted">{plan.analyzed ? 'measured (EXPLAIN ANALYZE, read only)' : 'estimated: the query did not run'}{summary ? ` · ${summary}` : ''}</span>
        <span className="data-grow" />
        {canMeasure && !plan.analyzed && (
          <button className="data-btn" disabled={measuring} onClick={onMeasure} title="Runs the query inside a read-only transaction to time it (EXPLAIN ANALYZE)" type="button">
            {measuring ? 'Measuring…' : 'Measure it'}
          </button>
        )}
      </div>
      {plan.notes?.length > 0 && (
        <ul className="data-notes data-plan-notes" data-testid="plan-notes">
          {plan.notes.map((note) => <li key={note}><span className={`data-dot ${/whole|every|no index|in memory/.test(note) ? 'is-orange' : 'is-green'}`} />{note}</li>)}
        </ul>
      )}
      <ul aria-label="Query plan" className="data-plan" role="tree">
        <PlanNode depth={0} node={plan.plan} />
      </ul>
    </div>
  );
}

PlanResult.propTypes = { canMeasure: PropTypes.bool, measuring: PropTypes.bool, onMeasure: PropTypes.func, plan: PropTypes.object.isRequired };

export function SaveSnippetDialog({ initialName = '', busy = false, error = null, onSave, onClose, title = 'Save as snippet', action = 'Save' }) {
  const [name, setName] = useState(initialName);
  return (
    <DataDialog
      footer={(
        <>
          <span className="data-grow" />
          <button className="data-btn" onClick={onClose} type="button">Cancel</button>
          <button className="data-btn is-primary" data-testid="snippet-save" disabled={busy || !name.trim()} form="data-snippet-form" type="submit">{busy ? 'Saving…' : action}</button>
        </>
      )}
      onClose={onClose}
      subtitle="Snippets are kept in the project: everyone in it sees them."
      testId="snippet-dialog"
      title={title}
    >
      <form id="data-snippet-form" onSubmit={(e) => { e.preventDefault(); if (name.trim()) { onSave(name.trim()); } }}>
        <label className="data-label" htmlFor="data-snippet-name">Name</label>
        <input autoFocus className="data-input is-wide" data-autofocus id="data-snippet-name" maxLength={120} onChange={(e) => setName(e.target.value)} value={name} />
      </form>
      {error && <p className="data-error-text" role="alert">{error}</p>}
    </DataDialog>
  );
}

SaveSnippetDialog.propTypes = {
  action: PropTypes.string, busy: PropTypes.bool, error: PropTypes.string, initialName: PropTypes.string, onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired, title: PropTypes.string,
};

export function SnippetsCard({ snippets, error = null, canEdit = false, canRun = () => true, onLoad, onRun, onRename, onDelete }) {
  const [open, setOpen] = useState(true);
  const [renaming, setRenaming] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const act = async (fn) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      setRenaming(null);
      setDeleting(null);
    } catch (e) {
      setActionError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (!snippets.length && !error) { return null; }
  return (
    <section aria-label="Snippets" className="data-card data-snippets" data-testid="snippets">
      <div className="data-card-head">
        <button aria-expanded={open} className="data-btn is-ghost" onClick={() => setOpen(!open)} type="button">
          {open ? <CaretDown aria-hidden size={12} /> : <CaretRight aria-hidden size={12} />}Snippets<span className="data-group-count">{snippets.length}</span>
        </button>
        <span className="data-muted">Saved in the project, shared with everyone in it</span>
      </div>
      {error && <p className="data-error-text" role="alert">{error}</p>}
      {open && (
        <ul className="data-plain-list data-snippet-list">
          {!snippets.length && <li><EmptyState title="No snippets yet" /></li>}
          {snippets.map((s) => (
            <li className="data-snippet" data-testid="snippet" key={s.id}>
              <button className="data-link data-snippet-name" onClick={() => onLoad(s)} title={s.query} type="button">{s.name}</button>
              <span className="data-pill">{LANGUAGE_LABELS[s.language] || s.language}</span>
              <span className="data-mono data-muted data-snippet-query">{s.query.replace(/\s+/g, ' ').slice(0, 90)}</span>
              <span className="data-grow" />
              <button aria-label={`Run ${s.name}`} className="data-btn is-tiny" disabled={!canRun(s)} onClick={() => onRun(s)} type="button"><Play aria-hidden size={12} />Run</button>
              {canEdit && <button aria-label={`Rename ${s.name}`} className="data-btn is-ghost is-tiny" onClick={() => setRenaming(s)} type="button"><PencilSimple aria-hidden size={13} /></button>}
              {canEdit && <button aria-label={`Delete ${s.name}`} className="data-btn is-ghost is-tiny" onClick={() => setDeleting(s)} type="button"><Trash aria-hidden size={13} /></button>}
            </li>
          ))}
        </ul>
      )}
      {renaming && (
        <SaveSnippetDialog
          action="Rename"
          busy={busy}
          error={actionError}
          initialName={renaming.name}
          onClose={() => { setRenaming(null); setActionError(null); }}
          onSave={(name) => act(() => onRename(renaming, name))}
          title={`Rename “${renaming.name}”`}
        />
      )}
      {deleting && (
        <ConfirmDialog busy={busy} error={actionError} onClose={() => { setDeleting(null); setActionError(null); }} onConfirm={() => act(() => onDelete(deleting))} title={`Delete the snippet “${deleting.name}”?`}>
          <p>It goes for everyone in the project.</p>
        </ConfirmDialog>
      )}
    </section>
  );
}

SnippetsCard.propTypes = {
  canEdit: PropTypes.bool, canRun: PropTypes.func, error: PropTypes.string, onDelete: PropTypes.func.isRequired, onLoad: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired, onRun: PropTypes.func.isRequired, snippets: PropTypes.array.isRequired,
};
